import DOMPurify from 'dompurify';

import { DescriptionSourceEnum } from '../types/shell';
import { descriptionToText } from './sanitize';

// DOMPurify's defaults permit <form>/<input>, i.e. a phishing form on a public page.
const ALLOWED_TAGS = [
  'p',
  'br',
  'span',
  'div',
  'b',
  'strong',
  'i',
  'em',
  'u',
  's',
  'strike',
  'ol',
  'ul',
  'li',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote',
  'a',
  'img',
];
const ALLOWED_ATTR = ['href', 'target', 'rel', 'src', 'alt', 'width', 'height', 'title', 'style'];

const SVG_DATA_URI = /^data:image\/svg/i;

// The editor writes underline and alignment into `style`, so dropping it loses 4 toolbar
// buttons — but keeping it as authored allows a `position: fixed` full-page overlay.
const ALLOWED_CSS_PROPS = new Set(['text-decoration', 'text-align']);

// Keywords are all these two properties need; rules out `url()`, `expression()`, escapes.
const SAFE_CSS_VALUE = /^[a-z- ]+$/i;

const filterStyle = (node: Element) => {
  const declarations = node.getAttribute('style')?.split(';') ?? [];

  const kept = declarations
    .map((declaration) => {
      const separator = declaration.indexOf(':');
      const prop = declaration.slice(0, separator).trim().toLowerCase();
      const value = declaration.slice(separator + 1).trim();

      return separator > 0 && ALLOWED_CSS_PROPS.has(prop) && SAFE_CSS_VALUE.test(value) ? `${prop}: ${value}` : '';
    })
    .filter(Boolean);

  // An empty style attribute is still a selector target.
  if (kept.length) {
    node.setAttribute('style', kept.join('; '));
  } else {
    node.removeAttribute('style');
  }
};

const hardenNode = (node: Element) => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }

  // Inert in an <img>, but this markup is reused in email templates where it isn't.
  if (SVG_DATA_URI.test(node.getAttribute('src') ?? '')) {
    node.removeAttribute('src');
  }

  if (node.hasAttribute('style')) {
    filterStyle(node);
  }
};

// Hooks are global state on a module singleton, so scope ours to the call. Removal passes the
// reference because the no-arg overload pops whatever hook happens to be last.
export const sanitizeRichText = (html: string) => {
  DOMPurify.addHook('afterSanitizeAttributes', hardenNode);

  try {
    return DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR });
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes', hardenNode);
  }
};

// Deliberately not in ALLOWED_TAGS — DOMPurify unwraps them to their text. But detection must
// still match them, or a bare `<table>` takes the text path and renders as escaped `&lt;table&gt;`.
const EDITOR_ONLY_TAGS = ['table', 'thead', 'tbody', 'tr', 'td', 'th', 'pre', 'code', 'hr', 'sub', 'sup'];
const MARKUP_TAGS = [...ALLOWED_TAGS, ...EDITOR_ONLY_TAGS];

// Vendor HTML has no guaranteed shape (`Feature A<br>Feature B` is valid), so any real tag counts.
// A bare `<orders@acme.com>` or `<here>` is not a tag name we know, so such text stays plain.
const HTML_MARKUP = new RegExp(`<(${MARKUP_TAGS.join('|')})(\\s[^<>]*)?/?>|</(${MARKUP_TAGS.join('|')})>`, 'i');

export const isMarketplaceRichText = (text: string) => HTML_MARKUP.test(text);

const EDITOR_ROOT_BLOCK_TAGS = new Set([
  'DIV',
  'P',
  'UL',
  'OL',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'TABLE',
  'BLOCKQUOTE',
  'PRE',
  'HR',
]);

const isBlankTextNode = (node: ChildNode) => node.nodeType === Node.TEXT_NODE && !node.textContent?.trim();

const isEditorRootBlock = (node: ChildNode) =>
  node.nodeType === Node.ELEMENT_NODE && EDITOR_ROOT_BLOCK_TAGS.has(node.nodeName);

export const isInventoryRichText = (text: string) => {
  const trimmed = text.trim();
  if (!trimmed.startsWith('<')) {
    return false;
  }

  const nodes = Array.from(new DOMParser().parseFromString(trimmed, 'text/html').body.childNodes);

  return nodes.some(isEditorRootBlock) && nodes.every((node) => isEditorRootBlock(node) || isBlankTextNode(node));
};

export const isDescriptionHtml = (text: string, source: DescriptionSourceEnum) =>
  source === DescriptionSourceEnum.Inventory ? isInventoryRichText(text) : isMarketplaceRichText(text);

export const normalizeLineBreaks = (text: string) => text.replace(/\r\n?/g, '\n');

export const hasDescriptionContent = (text: string | undefined, source: DescriptionSourceEnum) => {
  const value = text ?? '';

  return isDescriptionHtml(value, source) ? !!descriptionToText(value) : !!value.trim();
};
